import { Countries } from '@bridebook/models';
import type { ICost } from '@bridebook/models/source/models/Countries/Costs.types';
import type { CountryCodes } from '@bridebook/toolbox/src/gazetteer';
import { useQuery } from '@tanstack/react-query';

/**
 * The country cost templates with their `targets`: the allocation percentages by season,
 * weekday and budget band. The shared budget slice strips `targets` and may not be loaded when
 * the planner mounts, so the planner fetches its own copy. This keeps extra weight out of the
 * shared slice. The read is cache-first (a Firestore named query from IndexedDB), with infinite
 * staleness, since a template changes only every few months.
 */
export const useCountryCostsQuery = (
  country: string | null | undefined,
  enabled: boolean,
): { costs: Record<string, ICost> | undefined; settled: boolean; failed: boolean } => {
  const active = Boolean(country) && enabled;
  const { data, isFetched, isError } = useQuery(
    ['ai-planner', 'country-costs', country],
    () => Countries._.getById(country as CountryCodes).getCosts(undefined, true),
    { enabled: active, staleTime: Infinity, cacheTime: Infinity },
  );

  // A read nobody asked for has nothing to wait on, so that counts as settled.
  return { costs: data, settled: !active || isFetched, failed: isError };
};

export const useCountryCosts = (
  country: string | null | undefined,
  enabled: boolean,
): Record<string, ICost> | undefined => useCountryCostsQuery(country, enabled).costs;
